import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Switch, FormControlLabel,
  CircularProgress, Divider, MenuItem, Select, InputLabel,
  FormControl, IconButton, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useNavigate, useLocation } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import { updateFoodItem, createFoodItem, getCategories } from '../services/menuService';
import { fileService } from '../services/fileService';

const getDefaultFormData = (foodItem) => ({
  name: foodItem?.name || '',
  description: foodItem?.description || '',
  category: foodItem?.category?._id || foodItem?.category || '',
  isAvailable: foodItem?.isAvailable !== false,
  dishType: foodItem?.dishType || 'veg',
  taxSlab: foodItem?.taxSlab ?? 5,
  hasVariants: foodItem?.hasVariants || false,
  price: foodItem?.price ? foodItem.price.toString() : '',
  variants: foodItem?.variants?.length
    ? foodItem.variants.map(v => ({ ...v, price: v.price ? v.price.toString() : '' }))
    : [{ label: 'Regular', price: '' }],
  options: foodItem?.options || [],
  addOns: foodItem?.addOns?.length
    ? foodItem.addOns.map(a => ({ ...a, price: a.price ? a.price.toString() : '' }))
    : [],
  preparationTime: foodItem?.preparationTime ? foodItem.preparationTime.toString() : '',
  packagingCharge: foodItem?.packagingCharge ? foodItem.packagingCharge.toString() : '',
  includesTax: foodItem?.includesTax || false,
  recommended: foodItem?.recommended || false,
  image: foodItem?.image || [],
});

const TAX_OPTIONS = [
  { value: 0, label: '0% (No Tax)' },
  { value: 5, label: '5% (Standard)' },
  { value: 12, label: '12% (Reduced)' },
  { value: 18, label: '18% (Standard)' },
];

const SectionCard = ({ title, children, action }) => (
  <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, p: 3, mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1F2937' }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const FoodDetailPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { foodItem, branchId } = state || {};
  const isAddMode = !foodItem || !foodItem._id;

  const [formData, setFormData] = useState(() => getDefaultFormData(foodItem));
  const [categories, setCategories] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertActions, setAlertActions] = useState([]);

  const showAlert = (title, message, actions = [{ text: 'OK', variant: 'primary', onPress: () => setAlertVisible(false) }]) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertActions(actions);
    setAlertVisible(true);
  };

  useEffect(() => {
    if (!branchId) return;
    getCategories(branchId).then(setCategories).catch(() => {});
  }, [branchId]);

  useEffect(() => {
    const ids = foodItem?.image;
    if (!ids || !Array.isArray(ids) || ids.length === 0) return;
    Promise.all(ids.map(id => fileService.downloadFile(id).then(url => [id, url])))
      .then(pairs => {
        const map = {};
        pairs.forEach(([id, url]) => { if (url) map[id] = url; });
        setImageUrls(map);
      })
      .catch(() => {});
  }, [foodItem]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleImagePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadingImage(true);
      const uploadResponse = await fileService.uploadFile(file, 'food-item', 'menu');
      const id = uploadResponse.data._id;
      const url = await fileService.downloadFile(id);
      setImageUrls(prev => ({ ...prev, [id]: url }));
      set('image', [...(formData.image || []), id]);
    } catch (e) {
      showAlert('Upload Error', e.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (id) => {
    set('image', (formData.image || []).filter(imgId => imgId !== id));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { showAlert('Error', 'Name is required'); return; }
    if (!formData.category) { showAlert('Error', 'Category is required'); return; }
    if (!formData.hasVariants && (!formData.price || isNaN(Number(formData.price)))) {
      showAlert('Error', 'Valid price is required'); return;
    }
    try {
      setSaving(true);
      const payload = {
        ...formData,
        price: formData.price ? Number(formData.price) : undefined,
        preparationTime: formData.preparationTime ? Number(formData.preparationTime) : undefined,
        packagingCharge: formData.packagingCharge ? Number(formData.packagingCharge) : undefined,
        variants: formData.variants.map(v => ({ ...v, price: Number(v.price) })),
        addOns: formData.addOns.map(a => ({ ...a, price: Number(a.price) })),
      };
      if (isAddMode) {
        await createFoodItem(branchId, payload);
        showAlert('Success', 'Food item added', [{ text: 'OK', variant: 'primary', onPress: () => { setAlertVisible(false); navigate(-1); } }]);
      } else {
        await updateFoodItem(foodItem._id, payload, branchId);
        showAlert('Success', 'Food item updated', [{ text: 'OK', variant: 'primary', onPress: () => { setAlertVisible(false); navigate(-1); } }]);
      }
    } catch (e) {
      showAlert('Error', isAddMode ? 'Failed to add food item' : 'Failed to update food item');
    } finally {
      setSaving(false);
    }
  };

  // Variant helpers
  const addVariant = () => set('variants', [...formData.variants, { label: '', price: '' }]);
  const removeVariant = (i) => {
    if (formData.variants.length <= 1) return;
    set('variants', formData.variants.filter((_, idx) => idx !== i));
  };
  const setVariant = (i, field, val) => {
    const v = [...formData.variants];
    v[i] = { ...v[i], [field]: val };
    set('variants', v);
  };

  // Option helpers
  const addOption = () => set('options', [...formData.options, { option: '', choices: [''] }]);
  const removeOption = (i) => set('options', formData.options.filter((_, idx) => idx !== i));
  const setOptionName = (i, val) => {
    const o = [...formData.options];
    o[i] = { ...o[i], option: val };
    set('options', o);
  };
  const addChoice = (i) => {
    const o = [...formData.options];
    o[i] = { ...o[i], choices: [...o[i].choices, ''] };
    set('options', o);
  };
  const setChoice = (oi, ci, val) => {
    const o = [...formData.options];
    const choices = [...o[oi].choices];
    choices[ci] = val;
    o[oi] = { ...o[oi], choices };
    set('options', o);
  };

  // Add-on helpers
  const addAddOn = () => set('addOns', [...formData.addOns, { name: '', price: '' }]);
  const removeAddOn = (i) => set('addOns', formData.addOns.filter((_, idx) => idx !== i));
  const setAddOn = (i, field, val) => {
    const a = [...formData.addOns];
    a[i] = { ...a[i], [field]: val };
    set('addOns', a);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pb: 10 }}>
      <AppHeader title={isAddMode ? 'Add Food Item' : 'Edit Food Item'} />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        actions={alertActions}
        onRequestClose={() => setAlertVisible(false)}
      />

      <Box sx={{ p: 2, maxWidth: 680, mx: 'auto' }}>

        {/* Basic Info */}
        <SectionCard title="Basic Information">
          <TextField
            label="Name *"
            fullWidth
            value={formData.name}
            onChange={e => set('name', e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={e => set('description', e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category *</InputLabel>
            <Select
              value={formData.category}
              label="Category *"
              onChange={e => set('category', e.target.value)}
            >
              {categories.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 1 }}>Dish Type</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {['veg', 'non-veg'].map(type => (
              <Box
                key={type}
                onClick={() => set('dishType', type)}
                sx={{
                  flex: 1,
                  border: '1px solid',
                  borderColor: formData.dishType === type ? '#851E41' : '#D1D5DB',
                  borderRadius: 2,
                  p: 1.5,
                  cursor: 'pointer',
                  bgcolor: formData.dishType === type ? '#F8E9EE' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{
                  width: 14, height: 14, borderRadius: '50%',
                  bgcolor: formData.dishType === type
                    ? (type === 'veg' ? '#4CAF50' : '#F44336')
                    : '#D1D5DB'
                }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: formData.dishType === type ? '#851E41' : '#6B7280' }}>
                  {type === 'veg' ? 'Veg' : 'Non-Veg'}
                </Typography>
              </Box>
            ))}
          </Box>

          <FormControlLabel
            control={<Switch checked={formData.isAvailable} onChange={e => set('isAvailable', e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' } }} />}
            label="Available"
          />
        </SectionCard>

        {/* Images */}
        <SectionCard title="Images">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {(formData.image || []).map((id) => (
              <Box key={id} sx={{ position: 'relative', width: 96, height: 96, borderRadius: 2, overflow: 'hidden', border: '1px solid #E5E7EB', bgcolor: '#F3F4F6' }}>
                {imageUrls[id] ? (
                  <Box component="img" src={imageUrls[id]} alt="food" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <CircularProgress size={20} sx={{ color: '#851E41' }} />
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => removeImage(id)}
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }, p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: '#fff' }} />
                </IconButton>
              </Box>
            ))}
            <Button
              component="label"
              disabled={uploadingImage}
              sx={{
                width: 96, height: 96, borderRadius: 2, border: '1px dashed #851E41',
                display: 'flex', flexDirection: 'column', gap: 0.5, color: '#851E41',
              }}
            >
              {uploadingImage ? (
                <CircularProgress size={20} sx={{ color: '#851E41' }} />
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 24 }} />
                  <Typography sx={{ fontSize: 11 }}>Add</Typography>
                </>
              )}
              <input type="file" accept="image/*" hidden onChange={handleImagePick} />
            </Button>
          </Box>
        </SectionCard>

        {/* Pricing */}
        <SectionCard title="Pricing">
          <FormControlLabel
            control={<Switch checked={formData.hasVariants} onChange={e => set('hasVariants', e.target.checked)} />}
            label="Has Variants"
            sx={{ mb: 2 }}
          />

          {!formData.hasVariants ? (
            <TextField
              label="Price *"
              fullWidth
              type="number"
              value={formData.price}
              onChange={e => set('price', e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Variants</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addVariant} sx={{ color: '#851E41' }}>Add</Button>
              </Box>
              {formData.variants.map((v, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <TextField label="Label" size="small" value={v.label} onChange={e => setVariant(i, 'label', e.target.value)} sx={{ flex: 2 }} />
                  <TextField label="Price" size="small" type="number" value={v.price} onChange={e => setVariant(i, 'price', e.target.value)} sx={{ flex: 1 }} />
                  {formData.variants.length > 1 && (
                    <IconButton size="small" onClick={() => removeVariant(i)} sx={{ color: '#EF4444' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Tax Slab</InputLabel>
            <Select value={formData.taxSlab} label="Tax Slab" onChange={e => set('taxSlab', e.target.value)}>
              {TAX_OPTIONS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={formData.includesTax} onChange={e => set('includesTax', e.target.checked)} />}
            label="Tax Inclusive"
          />
          {formData.includesTax && (
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.5 }}>
              Price already includes tax — no additional tax will be calculated.
            </Typography>
          )}
        </SectionCard>

        {/* Options */}
        <SectionCard
          title="Options"
          action={<Button size="small" startIcon={<AddIcon />} onClick={addOption} sx={{ color: '#851E41' }}>Add</Button>}
        >
          {formData.options.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', py: 1 }}>No options added</Typography>
          ) : formData.options.map((opt, oi) => (
            <Box key={oi} sx={{ mb: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField label="Option Name" size="small" value={opt.option} onChange={e => setOptionName(oi, e.target.value)} sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => removeOption(oi)} sx={{ color: '#EF4444' }}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', mb: 1 }}>Choices</Typography>
              {opt.choices.map((ch, ci) => (
                <TextField key={ci} size="small" fullWidth value={ch} onChange={e => setChoice(oi, ci, e.target.value)} placeholder={`Choice ${ci + 1}`} sx={{ mb: 1 }} />
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => addChoice(oi)} sx={{ color: '#851E41', fontSize: 12 }}>Add Choice</Button>
            </Box>
          ))}
        </SectionCard>

        {/* Add-ons */}
        <SectionCard
          title="Add-ons"
          action={<Button size="small" startIcon={<AddIcon />} onClick={addAddOn} sx={{ color: '#851E41' }}>Add</Button>}
        >
          {formData.addOns.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', py: 1 }}>No add-ons added</Typography>
          ) : formData.addOns.map((addon, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <TextField label="Name" size="small" value={addon.name} onChange={e => setAddOn(i, 'name', e.target.value)} sx={{ flex: 2 }} />
              <TextField label="Price" size="small" type="number" value={addon.price} onChange={e => setAddOn(i, 'price', e.target.value)} sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => removeAddOn(i)} sx={{ color: '#EF4444' }}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </SectionCard>

        {/* Additional */}
        <SectionCard title="Additional Information">
          <TextField
            label="Preparation Time (minutes)"
            fullWidth size="small" type="number"
            value={formData.preparationTime}
            onChange={e => set('preparationTime', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Packaging Charge"
            fullWidth size="small" type="number"
            value={formData.packagingCharge}
            onChange={e => set('packagingCharge', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={formData.recommended} onChange={e => set('recommended', e.target.checked)} />}
            label="Recommended Item"
          />
        </SectionCard>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving} startIcon={<ArrowBackIcon />}
            sx={{ borderColor: '#D1D5DB', color: '#374151' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? null : <SaveIcon />}
            sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' }, minWidth: 120 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (isAddMode ? 'Add Item' : 'Update')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FoodDetailPage;
